/**
 * Web端 即构SDK对接
 * @author lianghua.fu
 * @date 2025-02-20 14:46:01
 *
 * 对接文档：https://doc-zh.zego.im/article/1806
 */

import { UiModal, UiToast } from 'components';

import { debug, config, globalPageConfig, request, track } from 'lib';
import RtcBase, {
  DeviceInfo,
  IOptions,
  IPreviewOptions,
  IRtcBase,
} from './rtc-base';

const log = debug.create('RtcZegoWeb');
const error = debug.create('RtcZegoWeb:error');
const STATE_TYPE = ['start', 'error', 'retry'];

export default class RtcZego extends RtcBase implements IRtcBase {
  rtcType = 'zego';

  loginUserId: string = '';

  isLogin = false;

  streamType: string;

  musicInstance: any;

  client: any;

  viewConfig: any;

  pushRtmpUrl = '';

  streamId = '';

  init(options: IOptions) {
    this.options = options;
    this.trackName = `RtcZegoWeb_${this.options.roomTypeName}`;
    this.client = new window.ZegoClient();
    this.loginUserId = `${this.options.userId}_${this.options.roomTypeName}`;

    this.trackLog('init', options);

    this._initZego();
    this._loginZego();
  }

  _initZego() {
    const options = {
      appid: config.zegoAppId, // 必填，应用id，由即构分配
      idName: this.loginUserId, // 必填，用户自定义id，全局唯一
      nickName: this.options.nickName, // 必填，用户自定义昵称
      remoteLogLevel: 100, // 上传日志最低级别，建议跟 logLevel 一致
      logLevel: 2, // 日志级别，debug:0,info:1,warn:2,error:3,report:99,disable:100（数字越大，日志越少），建议选择 1
      server: [
        'wss://webliveroom2999925240-api.zego.im/ws',
        'wss://webliveroom2999925240-api-bak.zego.im/ws',
      ], // 必填，接入服务器地址，由即构分配
      logUrl: 'wss://weblogger2999925240-api.zego.im/log', // 必填，logServer 地址，由即构分配
      audienceCreateRoom: true,
    };
    this.trackLog('initClient', options);
    this.client.config(options);

    this.client.onKickOut = (err) => {
      this.trackLog('onKickOut');
      UiToast.info('您已重复进入当前房间，即将回到首页', () => {
        location.href = '/';
      });
    };
    // 流更新通知
    this.client.onStreamUpdated = (type, streamList) => {
      // 0:添加， 1:删除
      this.trackLog('onStreamUpdated', {
        type,
        streamList,
      });
    };

    // 拉流状态变更通知
    // type start:0, error:1, retry: 2
    this.client.onPlayStateUpdate = (type, streamId, error) => {
      this.trackLog('onPlayStateUpdate', {
        type: STATE_TYPE[type],
        streamId,
        error,
      });
    };

    // 推流状态变更通知
    // type start:0, error:1, retry: 2
    this.client.onPublishStateUpdate = (type, streamId, error) => {
      this.trackLog('onPublishStateUpdate', {
        type: STATE_TYPE[type],
        streamId,
        error,
      });

      // if (type === 0) {
      //   this.onUpdatePublishState(streamId, true)
      // } else if (type === 1) {
      //   this.onUpdatePublishState(streamId, false)
      // }
      if (type === 0) {
        this.isPublishing = true;
      } else {
        this.isPublishing = false;
      }

      this.options.onPublishStateUpdate?.(type === 0);
    };

    this.client.onPublishQualityUpdate = (streamId, streamQuality) => {
      let { videoBitrate, audioBitrate, videoFPS, audioFPS, videoTransferFPS } =
        streamQuality;

      videoBitrate |= 0;
      audioBitrate |= 0;
      videoFPS |= 0;
      audioFPS |= 0;
      videoTransferFPS |= 0;

      this.trackLog(
        'onPublishQualityUpdate',
        { streamId, streamQuality },
        { videoBitrate, audioBitrate, videoFPS, videoTransferFPS },
      );

      this.options.onPublishQualityUpdate?.({
        videoBitrate,
        audioBitrate,
        videoFPS,
        videoTransferFPS,
      });
    };

    this.client.setSoundLevelDelegate(true, 200);

    this.client.onSoundLevelUpdate = (soundLevelList) => {
      // log('onSoundLevelUpdate', soundLevelList);
      this.options.onSoundLevelUpdate?.(
        soundLevelList
          .filter((item) => {
            return item.streamID;
          })
          .map((item) => {
            return {
              streamId: item.streamID,
              soundLevel: item.soundLevel,
            };
          }),
      );
    };
  }

  async _loginZego() {
    request.cryolite
      .get('api/v5/zego/auth-token', {
        id_name: this.loginUserId,
      })
      .then(async (res: any) => {
        const token = res.data.auth_token;
        this.trackLog('loginZego', {
          roomId: this.options.roomId,
          token,
        });
        this.client.login(
          this.options.roomId,
          1,
          token,
          (streamList) => {
            this.onLogin(true);

            this.trackLog('loginZegoSuccess', { streamList });
          },
          (err) => {
            this.onLogin(false);
            console.error('login zego err', err);
          },
        );
      })
      .catch((err) => {
        this.trackLog('loginZegoError', {
          err,
        });
        UiToast.fail('获取房间推流参数异常');
      });
  }

  getDeviceList(): Promise<{
    cameras: DeviceInfo[];
    microphones: DeviceInfo[];
  }> {
    return new Promise((resolve, reject) => {
      console.log('getDeviceList', this, this.client);
      this.client.enumDevices(
        ({ microphones, cameras, speakers }) => {
          this.trackLog('getDeviceList', {
            cameras,
            microphones,
          });

          resolve({
            cameras: cameras.map((item) => {
              return {
                deviceName: item.label,
                deviceId: item.deviceId,
              };
            }),
            microphones: microphones.map((item) => {
              return {
                deviceName: item.label,
                deviceId: item.deviceId,
              };
            }),
          });
        },
        (err) => {
          this.trackLog('getDeviceListError', err);
          UiModal.alert('提示', '获取媒体设备信息异常');
        },
      );
    });
  }

  startPreview(opts: IPreviewOptions, streamType) {
    const {
      viewContainer,
      hasVideo,
      videoInput,
      videoInputName,
      audioInput,
      success,
      error,
      previewConfig = {},
    } = opts;

    if (this.isPreviewed) {
      this.stopPreview();
    }

    super.startPreview(opts, streamType);
    this.videoInputName = videoInputName;

    const config = this.getEncoderConfig(streamType, videoInputName);

    const viewConfig = {
      audio: true,
      audioInput: audioInput || null,
      video: true,
      videoInput: videoInput || null,
      videoQuality: 4,
      horizontal: false,
      ...config,
    };

    log('startPreview', { opts, viewConfig }, viewContainer);

    const video = viewContainer.querySelector('video');
    this.viewVideo = video!;

    this.viewConfig = viewConfig;

    this.client.startPreview(
      video,
      viewConfig,
      () => {
        this.trackLog('startPreview', viewConfig);
        this.onPreviewed(true);
        success && success();
      },
      (reason) => {
        UiToast.fail('推流预览失败，请开启摄像头、麦克风权限后重试');
        this.trackLog('startPreviewError', {
          config,
          reason,
        });
        this.onPreviewed(false);
        error && error();
      },
    );
  }

  rePreview(viewContainer: HTMLDivElement) {
    let video = viewContainer.querySelector('video');
    if (this.isPreviewed) {
      this.client.startPreview(video, this.viewConfig);
      this.trackLog('rePreview');
    }
  }

  stopPreview() {
    this.onPreviewed(false);
    this.client.stopPreview(this.viewVideo);
    this.trackLog('stopPreview');
  }

  async getSnapshot() {
    return this.viewVideo;
  }

  enableCamera(enable: boolean) {
    if (!this.isPreviewed) {
      log('enableCamera not previewed');
      return;
    }
    this.trackLog('enableCamera', { enable });
    this.client.enableCamera(this.viewVideo, enable);
  }

  enableMicrophone(enable: boolean) {
    if (!this.isPreviewed) {
      log('enableMicrophone not previewed');
      return;
    }
    this.trackLog('enableMicrophone', { enable });
    this.client.enableMicrophone(this.viewVideo, enable);
  }

  startPublishingStream() {
    if (this.isLogin) {
      log('startPublishingStream', `${this.options.userId}`);

      // this.streamId = `${extraInfo?.streamId || this.options.streamId}`;

      request.cryolite
        .get('api/v5/zego/push-key', {
          stream_id: this.options.streamId,
        })
        .then(async (res: any) => {
          this.pushRtmpUrl = res.data.push_url;

          this.trackLog('startPublishingStream', {
            cdnUrl: this.pushRtmpUrl,
          });
          this.client.startPublishingStream(
            this.options.streamId,
            this.viewVideo,
            JSON.stringify({}),
            {
              cdnUrl: this.pushRtmpUrl,
            },
          );
        })
        .catch((err) => {
          UiToast.fail('推流异常');
          console.error(err);
          this.trackLog('startPublishingStreamError', { err });
        });
    } else {
      UiToast.fail('推流状态异常');
    }
  }

  stopPublishingStream() {
    // 兼容网页端的开播工具，各场景自己处理。客户端个播默认预览
    // if (!window.inPcClient) {
    //   this.stopPreview();
    // }
    this.client?.stopPublishingStream(this.options.streamId);
    this.isPublishing = false;
    this.trackLog('stopPublishingStream');
    this.options.onStopPublishingStream?.();
  }

  startPlayingStream(streamId: string, viewContainer: HTMLDivElement, onFail) {
    let video = viewContainer.querySelector('video');

    const result = this.client?.startPlayingStream(`${streamId}`, video);

    this.trackLog('startPlayingStream', { streamId, result }, video);
    if (!result) {
      onFail?.();
    }

    return () => {
      this.client?.stopPlayingStream(`${streamId}`);
      this.trackLog('stopPlayingStream', { streamId });
    };
  }

  mutePlayStreamAudio(
    streamId: string,
    mute: boolean,
    video: HTMLVideoElement,
  ) {
    video.muted = !mute;
    this.trackLog('mutePlayStreamAudio', { streamId, mute });
  }

  updateStream(streamType: string, isJoinLive: boolean) {
    if (!this.isPublishing) {
      this.trackLog('updateStream', 'not publishing');
      return;
    }
    super.updateStream(streamType, isJoinLive);
    this.streamType = streamType;

    const constraints = {
      ...(isJoinLive
        ? globalPageConfig.getConfig().joinLine3A
        : globalPageConfig.getConfig().default3A),
      ...globalPageConfig.getClientStreamConfig({
        streamType,
        deviceName: this.videoInputName,
      }),
    };

    this.trackLog('updateStream', {
      streamType,
      isJoinLive,
      constraints,
      userId: this.options.userId,
    });

    const getResult = () => {
      let audioTracks;
      let isSame;

      if (this.viewVideo) {
        audioTracks = this.viewVideo.srcObject
          // @ts-ignore
          ?.getAudioTracks()[0]
          .getSettings();
        isSame =
          constraints.autoGainControl === audioTracks.autoGainControl &&
          constraints.echoCancellation === audioTracks.echoCancellation &&
          constraints.noiseSuppression === audioTracks.noiseSuppression;

        log(
          'updateStream result',
          this.viewVideo.videoHeight,
          this.viewVideo.videoWidth,
          {
            streamType,
            isJoinLive,
          },
        );

        const cb = () => {
          log(
            'updateStream second result',
            this.viewVideo.videoHeight,
            this.viewVideo.videoWidth,
          );
        };

        if (streamType === 'anchorRoomPortrait') {
          //! isJoinLive &&
          if (this.viewVideo.videoHeight < 1080) {
            setTimeout(() => {
              constraints.height = this.viewVideo.videoHeight;
              constraints.width = Math.floor(
                (this.viewVideo.videoHeight / 16) * 9,
              );
              this.client.setPublishStreamConstraints(
                this.options.streamId,
                constraints,
                cb,
              );
            }, 1000);
          }
        }
      }
      return {
        isSame,
        audioTracks,
      };
    };

    this.client.setPublishStreamConstraints(
      this.options.streamId,
      constraints,
      () => {
        this.trackLog('setPublishStreamConstraintsSuccess', {
          streamType: this.streamType,
          constraints,
          ...getResult(),
        });
      },
      (error) => {
        this.trackLog('setPublishStreamConstraintsError', {
          streamType: this.streamType,
          constraints,
          ...getResult(),
          error,
        });
      },
    );
  }

  destroy() {
    this.client.logout();
    this.onLogin(false);
    this.options.onDestroy?.();
    this.client.release();
    this.trackLog('destroy', '引擎销毁');
  }
}
